import React, { useCallback, useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { addDays, addMonths, format, startOfDay, parseISO } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { useTicketingModalRootStore } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import {
  useFilterTicketModalActions,
  filterTicketModalStore,
  useFilterTicketModalPendingState,
} from './filter-ticket-modal.store';
import { handleDomBubbleEvent } from '@/utils/dom';
import { useListTicketMemberQuery } from '../../../api/get-ticket-member.api';
import { useListLabelTicketQuery } from '@/features/ticketings/label-ticketing/api/get-list-label-ticket.api';
import { TTicketMember } from '../../../types/ticket.type';
import { TLabelTicket } from '@/features/ticketings/label-ticketing/types/label-ticket.type';

/**
 * Configuration constants
 */
const MEMBER_QUERY_LIMIT = 500;
const LABEL_QUERY_LIMIT = 500;
const DATE_FORMAT = 'MMM dd, yyyy';

/**
 * Props for the useFilterTicketModal hook
 */
export interface IUseFilterTicketModalProps {
  /** Optional space ID to filter tickets for a specific space */
  spaceId?: string;
}

/**
 * Helper function to compare two arrays for equality
 * More efficient than JSON.stringify for array comparison
 */
const arraysEqual = <T>(a: T[], b: T[]): boolean => {
  if (a.length !== b.length) return false;
  if (a.length === 0) return true;
  return a.every((val, index) => val === b[index]);
};

const useFilterTicketModal = ({ spaceId }: IUseFilterTicketModalProps = {}) => {
  // HOOKS
  const modalActions = useTicketingModalRootStore(useShallow((state) => state.actions));
  const filterActions = useFilterTicketModalActions();

  const {
    pendingTitleSearch,
    pendingCreatedAt,
    pendingIsCompleted,
    pendingIsNotCompleted,
    pendingIsNoMember,
    pendingIsNoLabelTicket,
    pendingCreatorTickets,
    pendingMemberTickets,
    pendingLabelTickets,
    pendingNoDate,
    pendingOverDue,
    pendingDueInNextDay,
    pendingDueInNextDays,
    pendingDueInNextMonth,
    pendingDueInLastWeeks,
    pendingDueInLastMonths,
    pendingDueInTimeFrame,
  } = useFilterTicketModalPendingState();

  // Track pending changes reactively with optimized comparison
  const hasChanges = filterTicketModalStore(
    useShallow((state) => {
      return (
        state.pendingTitleSearch !== state.titleSearch ||
        JSON.stringify(state.pendingCreatedAt) !== JSON.stringify(state.createdAt) ||
        state.pendingIsCompleted !== state.isCompleted ||
        state.pendingIsNotCompleted !== state.isNotCompleted ||
        state.pendingIsNoMember !== state.isNoMember ||
        state.pendingIsNoLabelTicket !== state.isNoLabelTicket ||
        !arraysEqual(state.pendingCreatorTickets, state.creatorTickets) ||
        !arraysEqual(state.pendingMemberTickets, state.memberTickets) ||
        !arraysEqual(state.pendingLabelTickets, state.labelTickets) ||
        state.pendingNoDate !== state.noDate ||
        state.pendingOverDue !== state.overDue ||
        state.pendingDueInNextDay !== state.dueInNextDay ||
        state.pendingDueInNextDays !== state.dueInNextDays ||
        state.pendingDueInNextMonth !== state.dueInNextMonth ||
        state.pendingDueInLastWeeks !== state.dueInLastWeeks ||
        state.pendingDueInLastMonths !== state.dueInLastMonths ||
        JSON.stringify(state.pendingDueInTimeFrame) !== JSON.stringify(state.dueInTimeFrame)
      );
    })
  );

  // Check if any filters are currently active
  const isHasFilter = useMemo(() => {
    return (
      (pendingTitleSearch?.trim()?.length ?? 0) > 0 ||
      (pendingCreatedAt.startDate && pendingCreatedAt.endDate) ||
      pendingIsCompleted ||
      pendingIsNotCompleted ||
      pendingIsNoMember ||
      pendingIsNoLabelTicket ||
      pendingCreatorTickets.length > 0 ||
      pendingMemberTickets.length > 0 ||
      pendingLabelTickets.length > 0 ||
      pendingNoDate ||
      pendingOverDue ||
      pendingDueInNextDay ||
      pendingDueInNextDays ||
      pendingDueInNextMonth ||
      pendingDueInLastWeeks ||
      pendingDueInLastMonths ||
      (pendingDueInTimeFrame.startDate && pendingDueInTimeFrame.endDate)
    );
  }, [
    pendingTitleSearch,
    pendingCreatedAt.startDate,
    pendingCreatedAt.endDate,
    pendingIsCompleted,
    pendingIsNotCompleted,
    pendingIsNoMember,
    pendingIsNoLabelTicket,
    pendingCreatorTickets.length,
    pendingMemberTickets.length,
    pendingLabelTickets.length,
    pendingNoDate,
    pendingOverDue,
    pendingDueInNextDay,
    pendingDueInNextDays,
    pendingDueInNextMonth,
    pendingDueInLastWeeks,
    pendingDueInLastMonths,
    pendingDueInTimeFrame.startDate,
    pendingDueInTimeFrame.endDate,
  ]);

  // Check if modal is open
  const isOpen = useMemo(() => {
    return modalActions.checkIsOpenModal('FilterTicketModal');
  }, [modalActions]);

  // Update space ID when spaceId prop changes
  useEffect(() => {
    if (spaceId) {
      filterActions.setCurrentSpaceId(spaceId);
    }
  }, [spaceId, filterActions]);

  // Initialize pending state when modal opens
  useEffect(() => {
    if (isOpen) {
      filterActions.resetPendingFilters();
    }
  }, [isOpen, filterActions]);

  // Ticket Members API
  const { data: listTicketMemberData, isLoading: isLoadingListTicketMemberData } = useListTicketMemberQuery({
    params: {
      page: 1,
      limit: MEMBER_QUERY_LIMIT,
      spaceId: spaceId || '',
      search: '',
    },
    queryConfig: { enabled: !!spaceId },
  });

  const listTicketMemberOptions = useMemo(() => {
    return listTicketMemberData?.payload?.rows ?? [];
  }, [listTicketMemberData]);

  // Convert pendingMemberTickets (array of user IDs) to option objects
  const selectedMemberOptions = useMemo(() => {
    if (!pendingMemberTickets.length || !listTicketMemberOptions.length) {
      return [];
    }
    return listTicketMemberOptions.filter((member: TTicketMember) => pendingMemberTickets.includes(member.userId));
  }, [pendingMemberTickets, listTicketMemberOptions]);

  // Label Tickets API
  const { data: listLabelTicketData, isLoading: isLoadingListLabelTicketData } = useListLabelTicketQuery({
    params: { page: 1, limit: LABEL_QUERY_LIMIT },
    queryConfig: { enabled: !!spaceId },
  });

  const listLabelTicketOptions = useMemo(() => {
    return listLabelTicketData?.payload?.rows ?? [];
  }, [listLabelTicketData]);

  // Convert pendingLabelTickets (array of label IDs) to option objects
  const selectedLabelOptions = useMemo(() => {
    if (!pendingLabelTickets.length || !listLabelTicketOptions.length) {
      return [];
    }
    return listLabelTicketOptions.filter((label: TLabelTicket) => pendingLabelTickets.includes(label.labelTicketId));
  }, [pendingLabelTickets, listLabelTicketOptions]);

  // Convert pendingCreatorTickets (array of user IDs) to option objects
  // Reusing member options since creators are also members/users
  const selectedCreatorOptions = useMemo(() => {
    if (!pendingCreatorTickets.length || !listTicketMemberOptions.length) {
      return [];
    }
    return listTicketMemberOptions.filter((member: TTicketMember) => pendingCreatorTickets.includes(member.userId));
  }, [pendingCreatorTickets, listTicketMemberOptions]);

  // Calculate dates for tooltips (memoized to prevent recalculation)
  const today = useMemo(() => startOfDay(new Date()), []);
  const nextDay = useMemo(() => addDays(today, 1), [today]);
  const nextWeek = useMemo(() => addDays(today, 7), [today]);
  const nextMonth = useMemo(() => addMonths(today, 1), [today]);

  // Format date helper function
  const formatDate = useCallback((date: Date) => format(date, DATE_FORMAT), []);

  // Convert pendingDueInTimeFrame (string format) to DateRange
  const pendingTimeFrameDateRange = useMemo<DateRange | undefined>(() => {
    if (!pendingDueInTimeFrame.startDate || !pendingDueInTimeFrame.endDate) {
      return undefined;
    }
    try {
      return {
        from: parseISO(pendingDueInTimeFrame.startDate),
        to: parseISO(pendingDueInTimeFrame.endDate),
      };
    } catch {
      return undefined;
    }
  }, [pendingDueInTimeFrame.startDate, pendingDueInTimeFrame.endDate]);

  // Convert pendingCreatedAt to DateRange
  const pendingCreatedAtDateRange = useMemo<DateRange | undefined>(() => {
    if (!pendingCreatedAt.startDate || !pendingCreatedAt.endDate) {
      return undefined;
    }
    try {
      return {
        from: parseISO(pendingCreatedAt.startDate),
        to: parseISO(pendingCreatedAt.endDate),
      };
    } catch {
      return undefined;
    }
  }, [pendingCreatedAt.startDate, pendingCreatedAt.endDate]);

  // HANDLERS - All wrapped in useCallback to prevent unnecessary re-renders

  /**
   * Closes the modal and resets pending filters to match current filters
   */
  const handleCloseModal = useCallback(() => {
    filterActions.resetPendingFilters();
    modalActions.closeModal('FilterTicketModal');
  }, [filterActions, modalActions]);

  /**
   * Handles cancel button click - closes modal without applying changes
   */
  const handleCancelModal = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      handleDomBubbleEvent(e);
      handleCloseModal();
    },
    [handleCloseModal]
  );

  /**
   * Handles filter button click - applies pending filters and closes modal
   */
  const handleFilter = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      handleDomBubbleEvent(e);
      filterActions.applyPendingFilters();
      handleCloseModal();
    },
    [filterActions, handleCloseModal]
  );

  /**
   * Handles "Completed" checkbox change
   * Unchecks "Not Completed" when "Completed" is checked
   */
  const handleIsCompletedChange = useCallback(
    (checked: boolean) => {
      filterActions.setPendingIsCompleted(checked === true);
      if (checked === true) {
        filterActions.setPendingIsNotCompleted(false);
      }
    },
    [filterActions]
  );

  /**
   * Handles "Not Completed" checkbox change
   * Unchecks "Completed" when "Not Completed" is checked
   */
  const handleIsNotCompletedChange = useCallback(
    (checked: boolean) => {
      filterActions.setPendingIsNotCompleted(checked === true);
      if (checked === true) {
        filterActions.setPendingIsCompleted(false);
      }
    },
    [filterActions]
  );

  /**
   * Handles "No Member" checkbox change
   * Clears member tickets when "No Members" is checked
   */
  const handleNoMemberChange = useCallback(
    (checked: boolean) => {
      filterActions.setPendingIsNoMember(checked === true);
      if (checked === true) {
        filterActions.setPendingMemberTickets([]);
      }
    },
    [filterActions]
  );

  /**
   * Handles member checkbox change (select all/none)
   */
  const handleMemberCheckboxChange = useCallback(
    (checked: boolean) => {
      if (checked === false) {
        filterActions.setPendingMemberTickets([]);
      }
    },
    [filterActions]
  );

  /**
   * Handles member selection change
   */
  const handleMemberChange = useCallback(
    (value: TTicketMember[]) => {
      const userIds = value.map((option: TTicketMember) => option.userId);
      filterActions.setPendingMemberTickets(userIds);
    },
    [filterActions]
  );

  /**
   * Clears all selected members
   */
  const handleMemberClearAll = useCallback(() => {
    filterActions.setPendingMemberTickets([]);
  }, [filterActions]);

  /**
   * Removes a specific member from selection
   */
  const handleMemberRemove = useCallback(
    (option: TTicketMember | null) => {
      if (option) {
        const newUserIds = pendingMemberTickets.filter((id: string) => id !== option.userId);
        filterActions.setPendingMemberTickets(newUserIds);
      }
    },
    [pendingMemberTickets, filterActions]
  );

  /**
   * Handles "No Date" checkbox change
   * Clears timeframe when selected
   */
  const handleNoDateChange = useCallback(
    (checked: boolean) => {
      filterActions.setPendingNoDate(checked === true);
      if (checked === true) {
        filterActions.setPendingDueInTimeFrame({ startDate: '', endDate: '' });
      }
    },
    [filterActions]
  );

  /**
   * Handles "Overdue" checkbox change
   * Clears timeframe when selected
   */
  const handleOverDueChange = useCallback(
    (checked: boolean) => {
      filterActions.setPendingOverDue(checked === true);
      if (checked === true) {
        filterActions.setPendingDueInTimeFrame({ startDate: '', endDate: '' });
      }
    },
    [filterActions]
  );

  /**
   * Handles "Due in next day" checkbox change
   * Makes it mutually exclusive with "Due in next week" and "Due in next month"
   * Clears timeframe when selected
   */
  const handleDueInNextDayChange = useCallback(
    (checked: boolean) => {
      filterActions.setPendingDueInNextDay(checked === true);
      if (checked === true) {
        filterActions.setPendingDueInNextDays(false);
        filterActions.setPendingDueInNextMonth(false);
        filterActions.setPendingDueInTimeFrame({ startDate: '', endDate: '' });
      }
    },
    [filterActions]
  );

  /**
   * Handles "Due in next days" checkbox change
   * Makes it mutually exclusive with "Due in next day" and "Due in next month"
   * Clears timeframe when selected
   */
  const handleDueInNextDaysChange = useCallback(
    (checked: boolean) => {
      filterActions.setPendingDueInNextDays(checked === true);
      if (checked === true) {
        filterActions.setPendingDueInNextDay(false);
        filterActions.setPendingDueInNextMonth(false);
        filterActions.setPendingDueInTimeFrame({ startDate: '', endDate: '' });
      }
    },
    [filterActions]
  );

  /**
   * Handles "Due in next month" checkbox change
   * Makes it mutually exclusive with "Due in next day" and "Due in next week"
   * Clears timeframe when selected
   */
  const handleDueInNextMonthChange = useCallback(
    (checked: boolean) => {
      filterActions.setPendingDueInNextMonth(checked === true);
      if (checked === true) {
        filterActions.setPendingDueInNextDay(false);
        filterActions.setPendingDueInNextDays(false);
        filterActions.setPendingDueInTimeFrame({ startDate: '', endDate: '' });
      }
    },
    [filterActions]
  );

  /**
   * Handles "No Label" checkbox change
   * Clears label tickets when "No Labels" is checked
   */
  const handleNoLabelTicketChange = useCallback(
    (checked: boolean) => {
      filterActions.setPendingIsNoLabelTicket(checked === true);
      if (checked === true) {
        filterActions.setPendingLabelTickets([]);
      }
    },
    [filterActions]
  );

  /**
   * Handles creator checkbox change (select all/none)
   */
  const handleCreatorCheckboxChange = useCallback(
    (checked: boolean) => {
      if (checked === false) {
        filterActions.setPendingCreatorTickets([]);
      }
    },
    [filterActions]
  );

  /**
   * Handles creator selection change
   */
  const handleCreatorChange = useCallback(
    (value: TTicketMember[]) => {
      const userIds = value.map((option: TTicketMember) => option.userId);
      filterActions.setPendingCreatorTickets(userIds);
    },
    [filterActions]
  );

  /**
   * Clears all selected creators
   */
  const handleCreatorClearAll = useCallback(() => {
    filterActions.setPendingCreatorTickets([]);
  }, [filterActions]);

  /**
   * Removes a specific creator from selection
   */
  const handleCreatorRemove = useCallback(
    (option: TTicketMember | null) => {
      if (option) {
        const newUserIds = pendingCreatorTickets.filter((id: string) => id !== option.userId);
        filterActions.setPendingCreatorTickets(newUserIds);
      }
    },
    [pendingCreatorTickets, filterActions]
  );

  /**
   * Handles label checkbox change (select all/none)
   */
  const handleLabelCheckboxChange = useCallback(
    (checked: boolean) => {
      if (checked === false) {
        filterActions.setPendingLabelTickets([]);
      }
    },
    [filterActions]
  );

  /**
   * Handles label selection change
   */
  const handleLabelChange = useCallback(
    (value: TLabelTicket[]) => {
      const labelIds = value.map((option: TLabelTicket) => option.labelTicketId);
      filterActions.setPendingLabelTickets(labelIds);
    },
    [filterActions]
  );

  /**
   * Clears all selected labels
   */
  const handleLabelClearAll = useCallback(() => {
    filterActions.setPendingLabelTickets([]);
  }, [filterActions]);

  /**
   * Removes a specific label from selection
   */
  const handleLabelRemove = useCallback(
    (option: TLabelTicket | null) => {
      if (option) {
        const newLabelIds = pendingLabelTickets.filter((id: string) => id !== option.labelTicketId);
        filterActions.setPendingLabelTickets(newLabelIds);
      }
    },
    [pendingLabelTickets, filterActions]
  );

  /**
   * Handles timeframe change from TimeFrameModal
   * When timeframe is selected, unchecks all other due date options
   */
  const handleTimeFrameChange = useCallback(
    (dateRange: DateRange) => {
      if (dateRange.from && dateRange.to) {
        filterActions.setPendingDueInTimeFrame({
          startDate: dateRange.from.toISOString(),
          endDate: dateRange.to.toISOString(),
        });
        // Uncheck all other due date options when timeframe is selected
        filterActions.setPendingNoDate(false);
        filterActions.setPendingOverDue(false);
        filterActions.setPendingDueInNextDay(false);
        filterActions.setPendingDueInNextDays(false);
        filterActions.setPendingDueInNextMonth(false);
      } else {
        filterActions.setPendingDueInTimeFrame({ startDate: '', endDate: '' });
      }
    },
    [filterActions]
  );

  /**
   * Handles timeframe clear
   */
  const handleTimeFrameClear = useCallback(() => {
    filterActions.setPendingDueInTimeFrame({ startDate: '', endDate: '' });
  }, [filterActions]);

  /**
   * Handles createdAt timeframe change from TimeFrameModal
   */
  const handleCreatedAtTimeFrameChange = useCallback(
    (dateRange: DateRange) => {
      if (dateRange.from && dateRange.to) {
        filterActions.setPendingCreatedAt({
          startDate: dateRange.from.toISOString(),
          endDate: dateRange.to.toISOString(),
        });
      } else {
        filterActions.setPendingCreatedAt({ startDate: '', endDate: '' });
      }
    },
    [filterActions]
  );

  /**
   * Handles createdAt timeframe clear
   */
  const handleCreatedAtTimeFrameClear = useCallback(() => {
    filterActions.setPendingCreatedAt({ startDate: '', endDate: '' });
  }, [filterActions]);

  /**
   * Handles title search input change
   */
  const handleTitleSearchChange = useCallback(
    (value: string) => {
      filterActions.setPendingTitleSearch(value ?? '');
    },
    [filterActions]
  );

  /**
   * Resets all pending filters to default values
   */
  const handleClearAll = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      handleDomBubbleEvent(e);
      filterActions.setPendingTitleSearch('');
      filterActions.setPendingCreatedAt({ startDate: '', endDate: '' });
      filterActions.setPendingIsCompleted(false);
      filterActions.setPendingIsNotCompleted(false);
      filterActions.setPendingIsNoMember(false);
      filterActions.setPendingIsNoLabelTicket(false);
      filterActions.setPendingCreatorTickets([]);
      filterActions.setPendingMemberTickets([]);
      filterActions.setPendingLabelTickets([]);
      filterActions.setPendingNoDate(false);
      filterActions.setPendingOverDue(false);
      filterActions.setPendingDueInNextDay(false);
      filterActions.setPendingDueInNextDays(false);
      filterActions.setPendingDueInNextMonth(false);
      filterActions.setPendingDueInLastWeeks(false);
      filterActions.setPendingDueInLastMonths(false);
      filterActions.setPendingDueInTimeFrame({ startDate: '', endDate: '' });
    },
    [filterActions]
  );

  return {
    // Modal state
    hasChanges,
    isOpen,
    isHasFilter,

    // Pending filter state
    pendingTitleSearch,
    pendingIsCompleted,
    pendingIsNotCompleted,
    pendingIsNoMember,
    pendingIsNoLabelTicket,
    pendingCreatorTickets,
    pendingMemberTickets,
    pendingLabelTickets,
    pendingNoDate,
    pendingOverDue,
    pendingDueInNextDay,
    pendingDueInNextDays,
    pendingDueInNextMonth,

    // Member data
    listTicketMemberOptions,
    selectedMemberOptions,
    isLoadingListTicketMemberData,

    // Creator data (reusing member options)
    selectedCreatorOptions,

    // Label data
    listLabelTicketOptions,
    selectedLabelOptions,
    isLoadingListLabelTicketData,

    // Date helpers
    today,
    nextDay,
    nextWeek,
    nextMonth,
    formatDate,
    pendingTimeFrameDateRange,
    pendingCreatedAtDateRange,

    // Modal handlers
    handleCloseModal,
    handleCancelModal,
    handleFilter,

    // Title search handler
    handleTitleSearchChange,

    // Status handlers
    handleIsCompletedChange,
    handleIsNotCompletedChange,

    // Member handlers
    handleNoMemberChange,
    handleMemberCheckboxChange,
    handleMemberChange,
    handleMemberClearAll,
    handleMemberRemove,

    // Creator handlers
    handleCreatorCheckboxChange,
    handleCreatorChange,
    handleCreatorClearAll,
    handleCreatorRemove,

    // Date handlers
    handleNoDateChange,
    handleOverDueChange,
    handleDueInNextDayChange,
    handleDueInNextDaysChange,
    handleDueInNextMonthChange,

    // Label handlers
    handleNoLabelTicketChange,
    handleLabelCheckboxChange,
    handleLabelChange,
    handleLabelClearAll,
    handleLabelRemove,

    // Timeframe handlers
    handleTimeFrameChange,
    handleTimeFrameClear,

    // CreatedAt timeframe handlers
    handleCreatedAtTimeFrameChange,
    handleCreatedAtTimeFrameClear,

    // Utility handlers
    handleClearAll,
  };
};

export default useFilterTicketModal;
